//@flow
import type { TableColumnTypes } from 'common/entity/generic/EntityImportContainer/types';
import RiderPersonaImportHelper from './RiderPersonaImportHelper';

const RIDERPERSONA_IMPORT_FORMAT: Array<TableColumnTypes> = [
    {
        titleKey: 'masterRiderPersonaProperties.id',
        id: 'id',
        disabled: true,
        fixed: true,
        required: true,
        templateValue: 'masterImportTemplateValues.masterRiderPersona.id'
    },
    {
        titleKey: 'masterRiderPersonaProperties.code',
        id: 'code',
        disabled: true,
        templateValue: 'masterImportTemplateValues.masterRiderPersona.code'
    },
    {
        titleKey: 'masterRiderPersonaProperties.name',
        id: 'name',
        required: true,
        templateValue: 'masterImportTemplateValues.masterRiderPersona.name'
    },
    {
        titleKey: 'masterRiderPersonaProperties.type',
        id: 'type',
        isHidden: ({ isFieldHidden }) => isFieldHidden(),
        required: true,
        templateValue: 'masterImportTemplateValues.masterRiderPersona.type',
        processCell: RiderPersonaImportHelper.processUpperCaseId,
        validateCell: RiderPersonaImportHelper.validateType
    },
    {
        titleKey: 'masterRiderPersonaProperties.fleetType',
        id: 'fleetType',
        required: true,
        templateValue:
            'masterImportTemplateValues.masterRiderPersona.fleetType',
        processCell: RiderPersonaImportHelper.processUpperCaseId,
        validateCell: RiderPersonaImportHelper.validateFleetType
    },
    {
        titleKey: 'masterRiderPersonaProperties.skillId',
        id: 'skills',
        templateValue: 'masterImportTemplateValues.masterRiderPersona.skills',
        processCell: RiderPersonaImportHelper.processSkills,
        validateCell: RiderPersonaImportHelper.validateSkills,
        serializeCell: RiderPersonaImportHelper.serializeSkills
    },
    {
        titleKey: 'masterRiderPersonaProperties.transporterId',
        id: 'transporterId',
        required: true,
        processCell: RiderPersonaImportHelper.processTransporterId,
        serializeCell: RiderPersonaImportHelper.serializeTransporterId,
        templateValue:
            'masterImportTemplateValues.masterRiderPersona.transporterId',
        getIdForAsyncValidation: transporterId => {
            return transporterId?.transporterId;
        },
        validateCellAsync: ({
            entity,
            header,
            onSuccess,
            onFailure,
            validateTransporterIds,
            clientId,
            t
        }) => {
            RiderPersonaImportHelper.validateEntityIdAsync({
                validator: validateTransporterIds,
                entityNameId: 'masterRider',
                clientId,
                onSuccess,
                onFailure,
                entity,
                header,
                t
            });
        }
    },
    {
        titleKey: 'masterRiderPersonaProperties.allowMultiTrip',
        id: 'allowMultiTrip',
        isHidden: ({ isFieldHidden }) => isFieldHidden(),
        templateValue:
            'masterImportTemplateValues.masterRiderPersona.booleanValue',
        processCell: RiderPersonaImportHelper.getBooleanProcessor(),
        validateCell: RiderPersonaImportHelper.getBooleanValidator()
    },
    {
        titleKey: 'masterRiderPersonaProperties.enableAuth',
        id: 'enableAuth',
        isHidden: ({ isFieldHidden }) => isFieldHidden(),
        templateValue:
            'masterImportTemplateValues.masterRiderPersona.booleanValue',
        processCell: RiderPersonaImportHelper.getBooleanProcessor(),
        validateCell: RiderPersonaImportHelper.getBooleanValidator()
    },
    {
        titleKey: 'masterRiderPersonaProperties.enableLotr',
        id: 'enableLotr',
        isHidden: ({ isFieldHidden }) => isFieldHidden(),
        templateValue:
            'masterImportTemplateValues.masterRiderPersona.booleanValue',
        processCell: RiderPersonaImportHelper.getBooleanProcessor(),
        validateCell: RiderPersonaImportHelper.getBooleanValidator()
    },
    {
        titleKey: 'masterRiderPersonaProperties.enablePjp',
        id: 'enablePjp',
        isHidden: ({ isFieldHidden }) => isFieldHidden(),
        templateValue:
            'masterImportTemplateValues.masterRiderPersona.booleanValue',
        processCell: RiderPersonaImportHelper.getBooleanProcessor(),
        validateCell: RiderPersonaImportHelper.getBooleanValidator()
    },
    {
        titleKey: 'masterRiderPersonaProperties.volumeUnit',
        id: 'volumeUnit',
        exportId: 'limits.volume.unit',
        accessor: ['limits', 'volume', 'unit'],
        templateValue:
            'masterImportTemplateValues.masterRiderPersona.volumeUnit',
        processCell: RiderPersonaImportHelper.processVolumeUnitType,
        validateCell: RiderPersonaImportHelper.validateVolumeUnit
    },
    {
        titleKey:
            'masterRiderPersonaProperties.limitsWithUnits.transactionTimePerTask',
        translationProps: ({ t }) => ({
            unit: t('translations:units.minutes')
        }),
        id: 'transactionTimePerTask',
        accessor: ['limits', 'transactionTimePerTask'],
        templateValue:
            'masterImportTemplateValues.masterRiderPersona.transactionTimePerTask',
        processCell: RiderPersonaImportHelper.processMinTransactionTime
    },
    {
        titleKey:
            'masterRiderPersonaProperties.limitsWithUnits.minAllowedLimit',
        translationProps: ({ t }) => {
            return {
                unit: t('translations:units.km'),
                limit: t('riderPersonaProperties.limits.distance')
            };
        },
        id: 'minDistance',
        accessor: ['limits', 'distance', 'lowerThresh'],
        templateValue:
            'masterImportTemplateValues.masterRiderPersona.minDistance',
        processCell: RiderPersonaImportHelper.processKiloMetersToMeters
    },
    {
        titleKey:
            'masterRiderPersonaProperties.limitsWithUnits.maxAllowedLimit',
        translationProps: ({ t }) => {
            return {
                unit: t('translations:units.km'),
                limit: t('riderPersonaProperties.limits.distance')
            };
        },
        id: 'maxDistance',
        accessor: ['limits', 'distance', 'higherThresh'],
        templateValue:
            'masterImportTemplateValues.masterRiderPersona.maxDistance',
        validateCell: RiderPersonaImportHelper.validateHigherThresh,
        processCell: RiderPersonaImportHelper.processKiloMetersToMeters
    },
    {
        titleKey:
            'masterRiderPersonaProperties.limitsWithUnits.minAllowedLimit',
        translationProps: ({ t }) => {
            return {
                unit: t('translations:units.kg'),
                limit: t('riderPersonaProperties.limits.weight')
            };
        },
        id: 'minWeight',
        accessor: ['limits', 'weight', 'lowerThresh'],
        templateValue: 'masterImportTemplateValues.masterRiderPersona.minWeight'
    },
    {
        titleKey:
            'masterRiderPersonaProperties.limitsWithUnits.maxAllowedLimit',
        translationProps: ({ t }) => {
            return {
                unit: t('translations:units.kg'),
                limit: t('riderPersonaProperties.limits.weight')
            };
        },
        id: 'maxWeight',
        accessor: ['limits', 'weight', 'higherThresh'],
        templateValue:
            'masterImportTemplateValues.masterRiderPersona.maxWeight',
        validateCell: RiderPersonaImportHelper.validateHigherThresh
    },
    {
        titleKey:
            'masterRiderPersonaProperties.limitsWithUnits.minAllowedLimit',
        translationProps: ({ t }) => {
            return {
                unit: t('translations:units.minutes'),
                limit: t('riderPersonaProperties.limits.travelTime')
            };
        },
        id: 'minTravelTime',
        accessor: ['limits', 'travelTime', 'lowerThresh'],
        templateValue:
            'masterImportTemplateValues.masterRiderPersona.minTravelTime',
        validateCell: RiderPersonaImportHelper.validateHigherThresh,
        processCell: RiderPersonaImportHelper.processMinutesToSeconds
    },
    {
        titleKey:
            'masterRiderPersonaProperties.limitsWithUnits.maxAllowedLimit',
        translationProps: ({ t }) => {
            return {
                unit: t('translations:units.minutes'),
                limit: t('riderPersonaProperties.limits.travelTime')
            };
        },
        id: 'maxTravelTime',
        accessor: ['limits', 'travelTime', 'higherThresh'],
        templateValue:
            'masterImportTemplateValues.masterRiderPersona.maxTravelTime',
        validateCell: RiderPersonaImportHelper.validateHigherThresh,
        processCell: RiderPersonaImportHelper.processMinutesToSeconds
    },
    {
        titleKey:
            'masterRiderPersonaProperties.limitsWithUnits.minAllowedLimit',
        translationProps: ({ t }) => {
            return {
                unit: t('translations:units.count'),
                limit: t('riderPersonaProperties.limits.task')
            };
        },
        id: 'minTask',
        accessor: ['limits', 'task', 'lowerThresh'],
        templateValue: 'masterImportTemplateValues.masterRiderPersona.minTask'
    },
    {
        titleKey:
            'masterRiderPersonaProperties.limitsWithUnits.maxAllowedLimit',
        translationProps: ({ t }) => {
            return {
                unit: t('translations:units.count'),
                limit: t('riderPersonaProperties.limits.task')
            };
        },
        id: 'maxTask',
        accessor: ['limits', 'task', 'higherThresh'],
        templateValue: 'masterImportTemplateValues.masterRiderPersona.maxTask',
        validateCell: RiderPersonaImportHelper.validateHigherThresh
    },
    {
        titleKey:
            'masterRiderPersonaProperties.limitsWithUnits.minAllowedLimit',
        translationProps: ({ t }) => {
            return {
                unit: t('translations:units.count'),
                limit: t('riderPersonaProperties.limits.location')
            };
        },
        id: 'minLocation',
        accessor: ['limits', 'location', 'lowerThresh'],
        templateValue:
            'masterImportTemplateValues.masterRiderPersona.minLocation'
    },
    {
        titleKey:
            'masterRiderPersonaProperties.limitsWithUnits.maxAllowedLimit',
        translationProps: ({ t }) => {
            return {
                unit: t('translations:units.count'),
                limit: t('riderPersonaProperties.limits.location')
            };
        },
        id: 'maxLocation',
        accessor: ['limits', 'location', 'higherThresh'],
        templateValue:
            'masterImportTemplateValues.masterRiderPersona.maxLocation',
        validateCell: RiderPersonaImportHelper.validateHigherThresh
    },
    {
        titleKey:
            'masterRiderPersonaProperties.limitsWithUnits.minAllowedLimit',
        translationProps: ({ t }) => {
            return {
                unit: t('translations:units.minutes'),
                limit: t('riderPersonaProperties.limits.totalTime')
            };
        },
        id: 'minTotalTime',
        accessor: ['limits', 'totalTime', 'lowerThresh'],
        templateValue:
            'masterImportTemplateValues.masterRiderPersona.minTravelTime',
        processCell: RiderPersonaImportHelper.processMinutesToSeconds
    },
    {
        titleKey:
            'masterRiderPersonaProperties.limitsWithUnits.maxAllowedLimit',
        translationProps: ({ t }) => {
            return {
                unit: t('translations:units.minutes'),
                limit: t('riderPersonaProperties.limits.totalTime')
            };
        },
        id: 'maxTotalTime',
        accessor: ['limits', 'totalTime', 'higherThresh'],
        templateValue:
            'masterImportTemplateValues.masterRiderPersona.maxTravelTime',
        processCell: RiderPersonaImportHelper.processMinutesToSeconds,
        validateCell: RiderPersonaImportHelper.validateHigherThresh
    },
    {
        titleKey:
            'masterRiderPersonaProperties.limitsWithoutUnits.minAllowedLimit',
        translationProps: ({ t }) => {
            return {
                limit: t('riderPersonaProperties.limits.volume')
            };
        },
        id: 'minVolume',
        accessor: ['limits', 'volume', 'lowerThresh'],
        templateValue: 'masterImportTemplateValues.masterRiderPersona.minVolume'
    },
    {
        titleKey:
            'masterRiderPersonaProperties.limitsWithoutUnits.maxAllowedLimit',
        translationProps: ({ t }) => {
            return {
                limit: t('riderPersonaProperties.limits.volume')
            };
        },
        id: 'maxVolume',
        accessor: ['limits', 'volume', 'higherThresh'],
        templateValue: 'masterImportTemplateValues.masterRiderPersona.maxVolume'
    }
];

export default RIDERPERSONA_IMPORT_FORMAT;
